import { initSentry } from '@edsn/sentry';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import type { Route } from './+types/root';
import './global.css';
import { Toaster2, TooltipProvider } from '@edsn/ds';
import { Suspense } from 'react';
import '@fontsource-variable/inter';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { nl } from 'date-fns/locale/nl';
import 'highlight.js/styles/github-dark.min.css';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { GlobalFetchingIndicator } from './components/global-fetching-indicator/GlobalFetchingIndicator';
import { Spinner } from './components/spinner/Spinner';
import { OidcInitializationGate } from './oidc';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,
      gcTime: 20 * 60 * 1000,
      throwOnError: true,
      retry: (failureCount, error) => {
        if (failureCount >= 2) {
          return false;
        }
        // Retry on 5xx errors
        if (error instanceof Response) {
          return error.status >= 500 && error.status < 600;
        }
        // Retry on network errors
        if (error instanceof Error) {
          return error.name === 'NetworkError';
        }
        // Do not retry on other errors
        return false;
      },
    },
  },
});

const sentryDSN = import.meta.env.VITE_SENTRY_DSN;

// Init sentry and set default maskAll to false
sentryDSN &&
  initSentry(
    {
      dsn: sentryDSN,
      tracePropagationTargets: [/^https:\/\/gateway(-(?:dev|acc))?\.edsn\.nl/],
    },
    false,
  );

export const links: Route.LinksFunction = () => [];

export const handle = {
  breadcrumb: () => 'Home',
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" href="/favicon.png" />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <OidcInitializationGate fallback={<Spinner />}>
      <TooltipProvider>
        <LocalizationProvider adapterLocale={nl} dateAdapter={AdapterDateFns}>
          <QueryClientProvider client={queryClient}>
            <GlobalFetchingIndicator />
            <TanStackDevtools
              plugins={[
                {
                  name: 'TanStack Query',
                  render: <ReactQueryDevtoolsPanel />,
                  defaultOpen: true,
                },
              ]}
            />

            <Suspense fallback={<Spinner />}>
              <Outlet />
            </Suspense>
            <Toaster2 richColors theme="light" />
          </QueryClientProvider>
        </LocalizationProvider>
      </TooltipProvider>
    </OidcInitializationGate>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  console.error(error);

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1 className="text-3xl font-bold">Er is iets misgegaan</h1>
      <p className="mt-4">
        Probeer het later opnieuw of neem contact op met de helpdesk als het
        probleem aanhoudt.
      </p>
    </main>
  );
}
